import { useEffect, useRef, useState } from 'react';

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-[#0f1419] overflow-hidden">
      {/* Flame particles */}
      <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute bottom-0 w-2 h-2 bg-[#d4af37] rounded-full opacity-60 animate-flame-rise"
            style={{
              left: `${(i * 5) + Math.random() * 5}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center">
          {/* Left column - Mjölnir */}
          <div className={`lg:col-span-2 flex justify-center transition-all duration-1000 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-80'}`}>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/061fd83da038b7d6ff748d8b4f2c75a2.jpg"
                alt="Mjölnir"
                className="w-96 h-96 object-contain animate-rotate-slow drop-shadow-[0_0_40px_rgba(0,212,255,0.5)]"
              />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.3)_0%,transparent_70%)]" />
            </div>
          </div>

          {/* Right column - Content */}
          <div className={`lg:col-span-3 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            {/* Decorative rune border */}
            <div className="absolute left-0 top-0 hidden lg:flex flex-col gap-10 text-[#d4af37] text-2xl opacity-50">
              <span>ᛟ</span>
              <span>ᛇ</span>
              <span>ᛈ</span>
            </div>

            <div className="lg:pl-12">
              <p className="text-[#d4af37] text-sm uppercase tracking-[0.3em] mb-4 font-semibold">
                THE LEGEND
              </p>
              
              <h2 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight bg-gradient-to-r from-white to-[#d4af37] bg-clip-text text-transparent" style={{ fontFamily: 'Cinzel, serif' }}>
                FORGED IN RAGNARÖK
              </h2>

              <div className="space-y-6 text-[#b8b8b8] text-lg leading-relaxed">
                <p className="text-[#d4af37] italic text-xl">
                  A token forged in the fires of Ragnarök, resurrected for the strongest believers in Web3.
                </p>
                
                <p className="text-justify">
                  Those who enter the gates of Valhalla rise again through community, honor, and power. In the ancient halls where warriors feast eternal, a new legend is born—one written not in blood, but in blockchain. VALHALLA is more than a token; it is a covenant between the brave and the bold.
                </p>

                <p className="text-justify">
                  Built on the principles of decentralization and community governance, VALHALLA represents the resurrection of true crypto values. No central authority, no manipulation—only the strength of those who believe. Join the brotherhood of digital warriors and claim your place in the hall of legends.
                </p>

                <p className="text-justify">
                  The gates are open. The mead flows eternal. The only question is: are you worthy?
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background glow */}
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-[#d4af37] rounded-full blur-[150px] opacity-20 pointer-events-none" />
    </section>
  );
}
